/**
 * Issues Page Component
 * Lists all issues for the authenticated client with filtering
 * Supports expandable cards to show article mentions
 */

import React, { useState, useEffect, useRef } from 'react';
import { Copy, ExternalLink, Mail, Share2, X } from 'lucide-react';
import { api } from '../api';
import './Issues.css';

interface Issue {
  id: string;
  title: string;
  description: string | null;
  priority: string;
  status: string;
  created_at: string;
  createdAt?: string;
  current_heat?: number;
  heat_delta?: number;
  heat_trend?: 'rising' | 'stable' | 'falling';
  source_article_count?: number | null;
  stakeholders?: Stakeholder[];
}

interface Stakeholder {
  id: string;
  name: string;
  profile_image: string | null;
  role: string;
  title: string | null;
  organization: string | null;
  category: string | null;
  client_relationship?: {
    relationship: string;
    interest_level: number | null;
    influence_level: number | null;
    notes: string | null;
  };
}

interface Mention {
  id: string;
  title: string;
  url: string;
  summary: string | null;
  source_name: string;
  published_at: string | null;
  sentiment_score: number | null;
  category: string | null;
  relevance: number | null;
}

const Issues: React.FC = () => {
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  
  // Expandable mentions state
  const [expandedIssueId, setExpandedIssueId] = useState<string | null>(null);
  const [mentions, setMentions] = useState<Record<string, Mention[]>>({});
  const [loadingMentions, setLoadingMentions] = useState<Record<string, boolean>>({});
  const [mentionSortBy, setMentionSortBy] = useState<'relevance' | 'date'>('relevance');
  const [selectionIssueId, setSelectionIssueId] = useState<string | null>(null);
  const [selectedMentionIds, setSelectedMentionIds] = useState<Set<string>>(new Set());
  const [shareMenuMentionId, setShareMenuMentionId] = useState<string | null>(null);
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);
  const shareMenuRef = useRef<HTMLDivElement>(null);
  const shareTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    loadIssues();
  }, [priorityFilter, statusFilter, dateFilter]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target as Node)) {
        setShareMenuMentionId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!shareMenuMentionId) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShareMenuMentionId(null);
        window.setTimeout(() => shareTriggerRef.current?.focus(), 0);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [shareMenuMentionId]);

  useEffect(() => {
    if (!shareFeedback) return;
    const timer = window.setTimeout(() => setShareFeedback(null), 3200);
    return () => window.clearTimeout(timer);
  }, [shareFeedback]);

  const loadIssues = async () => {
    try {
      setLoading(true);
      setError('');
      
      const filters: any = {};
      if (priorityFilter) filters.priority = priorityFilter;
      if (statusFilter) filters.status = statusFilter;
      
      const response = await api.getIssues(filters);
      setIssues(response.issues);
    } catch (err: any) {
      setError(err.message || 'Failed to load issues');
    } finally {
      setLoading(false);
    }
  };

  const loadMentions = async (issueId: string) => {
    // Don't reload if already loaded
    if (mentions[issueId]) return;
    
    try {
      setLoadingMentions(prev => ({ ...prev, [issueId]: true }));
      const response = await api.getIssueMentions(issueId);
      setMentions(prev => ({ ...prev, [issueId]: response.mentions }));
    } catch (err: any) {
      console.error('Failed to load mentions:', err);
      setMentions(prev => ({ ...prev, [issueId]: [] }));
    } finally {
      setLoadingMentions(prev => ({ ...prev, [issueId]: false }));
    }
  };

  const toggleIssue = async (issueId: string) => {
    if (expandedIssueId === issueId) {
      // Collapse if already expanded
      setExpandedIssueId(null);
      setShareMenuMentionId(null);
      if (selectionIssueId === issueId) {
        setSelectionIssueId(null);
        setSelectedMentionIds(new Set());
      }
    } else {
      // Expand and load mentions
      setExpandedIssueId(issueId);
      setShareMenuMentionId(null);
      setSelectionIssueId(null);
      setSelectedMentionIds(new Set());
      await loadMentions(issueId);
    }
  };

  const isTouchPrimaryDevice = () => (
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches
  );

  const buildMentionDigest = (mentionList: Mention[]) => mentionList
    .map((mention, index) => `${index + 1}. ${mention.title}\n${mention.source_name} · ${formatDate(mention.published_at)}\n${mention.url}`)
    .join('\n\n');

  const copyTextToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }

      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textArea);
      return copied;
    } catch {
      return false;
    }
  };

  const getSortedMentions = (issueId: string) => {
    const issueMentions = [...(mentions[issueId] || [])];
    return issueMentions.sort((left, right) => {
      if (mentionSortBy === 'date') {
        return new Date(right.published_at || 0).getTime() - new Date(left.published_at || 0).getTime();
      }
      return (right.relevance ?? Number.NEGATIVE_INFINITY) - (left.relevance ?? Number.NEGATIVE_INFINITY);
    });
  };

  const getSourceCoverage = (issueId: string) => {
    const counts = (mentions[issueId] || []).reduce<Record<string, number>>((accumulator, mention) => {
      const source = mention.source_name?.trim() || 'Unknown source';
      accumulator[source] = (accumulator[source] || 0) + 1;
      return accumulator;
    }, {});

    const rankedSources = Object.entries(counts)
      .sort(([, leftCount], [, rightCount]) => rightCount - leftCount)
      .slice(0, 3);

    return {
      totalSources: Object.keys(counts).length,
      rankedSources
    };
  };

  const toggleMentionSelectionMode = (issueId: string) => {
    if (selectionIssueId === issueId) {
      setSelectionIssueId(null);
      setSelectedMentionIds(new Set());
    } else {
      setSelectionIssueId(issueId);
      setSelectedMentionIds(new Set());
    }
    setShareMenuMentionId(null);
  };

  const toggleMentionSelection = (issueId: string, mentionId: string) => {
    if (selectionIssueId !== issueId) {
      setSelectionIssueId(issueId);
      setSelectedMentionIds(new Set([mentionId]));
      return;
    }

    setSelectedMentionIds(previous => {
      const next = new Set(previous);
      if (next.has(mentionId)) {
        next.delete(mentionId);
      } else {
        next.add(mentionId);
      }
      return next;
    });
  };

  const handleSingleMentionShare = async (mention: Mention, event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    shareTriggerRef.current = event.currentTarget;

    if (isTouchPrimaryDevice() && navigator.share) {
      try {
        await navigator.share({ title: mention.title, text: buildMentionDigest([mention]) });
        setShareFeedback('Article shared.');
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          setShareFeedback('Unable to open sharing options.');
        }
      }
      return;
    }

    setShareMenuMentionId(current => current === mention.id ? null : mention.id);
  };

  const handleCopyMentionLink = async (mention: Mention) => {
    const copied = await copyTextToClipboard(mention.url);
    setShareMenuMentionId(null);
    setShareFeedback(copied ? 'Link copied.' : 'Unable to copy the link.');
  };

  const handleEmailMention = (mention: Mention) => {
    const subject = encodeURIComponent(mention.title);
    const body = encodeURIComponent(buildMentionDigest([mention]));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
    setShareMenuMentionId(null);
  };

  const handleOpenMention = (mention: Mention) => {
    window.open(mention.url, '_blank', 'noopener,noreferrer');
    setShareMenuMentionId(null);
  };

  const handleBulkMentionShare = async (issueId: string) => {
    const selectedMentions = (mentions[issueId] || []).filter(mention => selectedMentionIds.has(mention.id));
    if (!selectedMentions.length) return;

    const digest = buildMentionDigest(selectedMentions);
    if (isTouchPrimaryDevice() && navigator.share) {
      try {
        await navigator.share({
          title: `${selectedMentions.length} issue mention${selectedMentions.length === 1 ? '' : 's'}`,
          text: digest
        });
        setShareFeedback('Selected articles shared.');
      } catch (error: any) {
        if (error?.name !== 'AbortError') {
          setShareFeedback('Unable to open sharing options.');
        }
      }
      return;
    }

    const copied = await copyTextToClipboard(digest);
    setShareFeedback(copied ? 'Article digest copied.' : 'Unable to copy the article digest.');
  };

  const getIssueTrendLabel = (trend: Issue['heat_trend']) => {
    if (trend === 'rising') return 'Rising';
    if (trend === 'falling') return 'Falling';
    return 'Stable';
  };

  const formatIssueCreatedDate = (issue: Issue): string => {
    const rawDate = issue.createdAt || issue.created_at;
    if (!rawDate) return 'Date unavailable';

    const parsedDate = new Date(rawDate);
    if (Number.isNaN(parsedDate.getTime())) return 'Date unavailable';

    return parsedDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getSentimentLabel = (score: number | null): string => {
    if (score === null) return 'neutral';
    if (score > 0) return 'positive';
    if (score < 0) return 'negative';
    return 'neutral';
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Unknown date';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Unknown date';
    }
  };

  const getDateRangeFilter = (days: string): Date | null => {
    if (days === 'all') return null;
    
    const now = new Date();
    const daysAgo = new Date(now);
    daysAgo.setDate(daysAgo.getDate() - parseInt(days));
    return daysAgo;
  };

  const isWithinDateRange = (dateString: string | null, days: string): boolean => {
    if (days === 'all' || !dateString) return true;
    
    const issueDate = new Date(dateString);
    const cutoffDate = getDateRangeFilter(days);
    
    return cutoffDate ? issueDate >= cutoffDate : true;
  };

  const filterIssues = (): Issue[] => {
    return issues.filter(issue => {
      // Search is applied client-side only (no API call)
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || 
        issue.title?.toLowerCase().includes(searchLower) ||
        issue.description?.toLowerCase().includes(searchLower) ||
        // Also search stakeholder names linked to this issue
        (issue.stakeholders && issue.stakeholders.some(stakeholder => 
          stakeholder.name.toLowerCase().includes(searchLower)
        ));
      // Other filters are already applied via API (but kept here for safety)
      const matchesPriority = !priorityFilter || issue.priority === priorityFilter;
      const matchesStatus = !statusFilter || issue.status === statusFilter;
      const matchesDate = isWithinDateRange(issue.created_at, dateFilter);
      
      return matchesSearch && matchesPriority && matchesStatus && matchesDate;
    });
  };

  const clearFilters = () => {
    setPriorityFilter('');
    setStatusFilter('');
    setSearchTerm('');
    setDateFilter('all');
  };

  const getTooltipContent = (stakeholder: Stakeholder): string => {
    const lines = [
      `Name: ${stakeholder.name}`,
      `Role: ${stakeholder.role}`,
      `Title: ${stakeholder.title || 'N/A'}`,
      `Organization: ${stakeholder.organization || 'N/A'}`,
      `Category: ${stakeholder.category || 'N/A'}`,
    ];
    
    if (stakeholder.client_relationship) {
      lines.push(`Relationship: ${stakeholder.client_relationship.relationship}`);
      if (stakeholder.client_relationship.interest_level !== null) {
        lines.push(`Interest Level: ${stakeholder.client_relationship.interest_level}/5`);
      }
      if (stakeholder.client_relationship.influence_level !== null) {
        lines.push(`Influence Level: ${stakeholder.client_relationship.influence_level}/5`);
      }
    }
    
    return lines.join('\n');
  };

  const StakeholderBadge: React.FC<{ stakeholder: Stakeholder }> = ({ stakeholder }) => (
    <div className="stakeholder-badge" title={getTooltipContent(stakeholder)}>
      {stakeholder.profile_image ? (
        <img 
          src={stakeholder.profile_image} 
          alt={stakeholder.name}
          className="stakeholder-image"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      ) : (
        <div className="stakeholder-image-placeholder">
          {stakeholder.name.charAt(0).toUpperCase()}
        </div>
      )}
      <span className="stakeholder-name">{stakeholder.name}</span>
    </div>
  );

  if (loading) {
    return (
      <div className="issues-loading">
        <div className="spinner"></div>
        <p>Loading issues...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="issues-error">
        <p>❌ {error}</p>
        <button onClick={loadIssues}>Retry</button>
      </div>
    );
  }

  return (
    <div className="issues-page">
      <div className="issues-header">
        <div>
          <h1>Issues</h1>
          <p className="issues-subtitle">
            {issues.length} {issues.length === 1 ? 'issue' : 'issues'} found
          </p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-input-wrapper">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search issues by title or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="search-clear-btn"
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="issues-filters">
        <div className="filter-group">
          <label htmlFor="priority-filter">Priority</label>
          <select
            id="priority-filter"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <option value="">All priorities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="status-filter">Status</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="date-filter">Date Range</label>
          <select
            id="date-filter"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            <option value="all">All time</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>

        {(priorityFilter || statusFilter || searchTerm || dateFilter !== 'all') && (
          <button onClick={clearFilters} className="clear-filters-btn">
            Clear filters
          </button>
        )}
      </div>

      {/* Issues list */}
      {filterIssues().length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No issues found</h3>
          <p>There are no issues matching your criteria.</p>
        </div>
      ) : (
        <div className="issues-list">
          {filterIssues().map((issue) => (
            <div 
              key={issue.id} 
              className={`issue-card ${expandedIssueId === issue.id ? 'expanded' : ''}`}
            >
              <div 
                className="issue-card-clickable"
                onClick={() => toggleIssue(issue.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    toggleIssue(issue.id);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-expanded={expandedIssueId === issue.id}
                aria-label={`${expandedIssueId === issue.id ? 'Collapse' : 'Expand'} issue: ${issue.title}`}
              >
                <div className="issue-card-header">
                  <h3 className="issue-card-title">{issue.title}</h3>
                  <div className="issue-card-badges">
                    {typeof issue.current_heat === 'number' && (
                      <span className="issue-heat-badge" title="Current HEAT score">
                        HEAT {issue.current_heat.toFixed(1)}
                      </span>
                    )}
                    <span className={`issue-trend-badge ${issue.heat_trend || 'stable'}`}>
                      {issue.heat_trend === 'rising' ? '▲' : issue.heat_trend === 'falling' ? '▼' : '●'} {getIssueTrendLabel(issue.heat_trend)}
                    </span>
                    <span className={`badge badge-priority-${issue.priority}`}>
                      {issue.priority}
                    </span>
                    <span className={`badge badge-status-${issue.status}`}>
                      {issue.status.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                
                {issue.description && (
                  <p className="issue-card-description">{issue.description}</p>
                )}

                {typeof issue.source_article_count === 'number' && (
                  <p className="issue-coverage-context">
                    Based on {issue.source_article_count} linked article{issue.source_article_count === 1 ? '' : 's'}.
                  </p>
                )}
                
                <div className="issue-card-footer">
                  <span className="issue-card-date">
                    Created {formatIssueCreatedDate(issue)}
                  </span>
                  <span className="issue-card-expand-hint">
                    {expandedIssueId === issue.id ? '▼ Click to collapse' : '▶ Click to view mentions'}
                  </span>
                </div>
                
                {/* Stakeholders section */}
                {issue.stakeholders && issue.stakeholders.length > 0 && (
                  <div className="issue-stakeholders">
                    <span className="stakeholders-label">👥 Stakeholders:</span>
                    <div className="stakeholders-row">
                      {issue.stakeholders.map(stakeholder => (
                        <StakeholderBadge key={stakeholder.id} stakeholder={stakeholder} />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Mentions section (shown when expanded) */}
              {expandedIssueId === issue.id && (
                <div className="issue-mentions" onClick={(e) => e.stopPropagation()}>
                  <div className="mentions-toolbar">
                    <h4 className="mentions-title">
                      📰 Mentions ({mentions[issue.id]?.length || 0})
                    </h4>
                    <div className="mentions-toolbar-actions">
                      <div className="mentions-sort-toggle" role="group" aria-label="Sort mentions">
                        <button
                          type="button"
                          className={mentionSortBy === 'relevance' ? 'active' : ''}
                          onClick={() => setMentionSortBy('relevance')}
                          aria-pressed={mentionSortBy === 'relevance'}
                        >
                          Relevance
                        </button>
                        <button
                          type="button"
                          className={mentionSortBy === 'date' ? 'active' : ''}
                          onClick={() => setMentionSortBy('date')}
                          aria-pressed={mentionSortBy === 'date'}
                        >
                          Date
                        </button>
                      </div>
                      <button
                        type="button"
                        className={`mentions-select-button ${selectionIssueId === issue.id ? 'active' : ''}`}
                        onClick={() => toggleMentionSelectionMode(issue.id)}
                        aria-pressed={selectionIssueId === issue.id}
                      >
                        <Share2 size={15} aria-hidden="true" />
                        {selectionIssueId === issue.id ? 'Selection active' : 'Select'}
                      </button>
                    </div>
                  </div>
                  {(() => {
                    const sourceCoverage = getSourceCoverage(issue.id);
                    if (!sourceCoverage.totalSources) return null;
                    return (
                      <p className="issue-source-coverage">
                        Covered by {sourceCoverage.totalSources} source{sourceCoverage.totalSources === 1 ? '' : 's'}
                        {sourceCoverage.rankedSources.length > 0 && ` · ${sourceCoverage.rankedSources.map(([source, count]) => `${source} (${count})`).join(', ')}`}
                      </p>
                    );
                  })()}
                  {shareFeedback && (
                    <p className="issues-share-feedback" role="status" aria-live="polite">{shareFeedback}</p>
                  )}
                  
                  {loadingMentions[issue.id] ? (
                    <div className="mentions-loading">
                      <div className="spinner-small"></div>
                      <span>Loading mentions...</span>
                    </div>
                  ) : mentions[issue.id]?.length === 0 ? (
                    <div className="mentions-empty">
                      <p>No article mentions found for this issue.</p>
                    </div>
                  ) : (
                    <>
                    <div className="mentions-list">
                      {getSortedMentions(issue.id).map(mention => (
                        <div key={mention.id} className={`mention-card ${selectionIssueId === issue.id ? 'selection-mode' : ''}`}>
                          {selectionIssueId === issue.id && (
                            <label className="mention-selection-control">
                              <input
                                type="checkbox"
                                checked={selectedMentionIds.has(mention.id)}
                                onChange={() => toggleMentionSelection(issue.id, mention.id)}
                                aria-label={`Select ${mention.title}`}
                              />
                            </label>
                          )}
                          <div className="mention-card-content">
                            <div className="mention-header">
                              <a 
                                href={mention.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="mention-title"
                              >
                                {mention.title}
                              </a>
                              <span className={`sentiment-badge sentiment-${getSentimentLabel(mention.sentiment_score)}`}>
                                {getSentimentLabel(mention.sentiment_score)}
                              </span>
                            </div>
                            
                            <div className="mention-meta">
                              <span className="mention-source">📄 {mention.source_name}</span>
                              <span className="mention-date">📅 {formatDate(mention.published_at)}</span>
                            </div>
                            
                            {mention.summary && (
                              <p className="mention-summary">{mention.summary}</p>
                            )}
                          </div>
                          <div
                            className="mention-share-control"
                            ref={shareMenuMentionId === mention.id ? shareMenuRef : null}
                          >
                            <button
                              type="button"
                              className="mention-share-button"
                              onClick={(event) => handleSingleMentionShare(mention, event)}
                              aria-label={`Share ${mention.title}`}
                              aria-expanded={shareMenuMentionId === mention.id}
                              aria-haspopup="menu"
                            >
                              <Share2 size={16} aria-hidden="true" />
                              <span>Share</span>
                            </button>
                            {shareMenuMentionId === mention.id && (
                              <div className="mention-share-menu" role="menu" aria-label={`Sharing options for ${mention.title}`}>
                                <button type="button" role="menuitem" onClick={() => handleCopyMentionLink(mention)}>
                                  <Copy size={15} aria-hidden="true" /> Copy link
                                </button>
                                <button type="button" role="menuitem" onClick={() => handleEmailMention(mention)}>
                                  <Mail size={15} aria-hidden="true" /> Share by email
                                </button>
                                <button type="button" role="menuitem" onClick={() => handleOpenMention(mention)}>
                                  <ExternalLink size={15} aria-hidden="true" /> Open article
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    {selectionIssueId === issue.id && selectedMentionIds.size > 0 && (
                      <div className="mentions-selection-action-bar" role="region" aria-label="Selected issue mention actions">
                        <span>{selectedMentionIds.size} selected</span>
                        <button type="button" className="mentions-selection-share" onClick={() => handleBulkMentionShare(issue.id)}>
                          <Share2 size={16} aria-hidden="true" />
                          Share {selectedMentionIds.size} article{selectedMentionIds.size === 1 ? '' : 's'}
                        </button>
                        <button type="button" className="mentions-selection-cancel" onClick={() => toggleMentionSelectionMode(issue.id)}>
                          <X size={16} aria-hidden="true" />
                          Cancel
                        </button>
                      </div>
                    )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Issues;
